import { useState } from 'react'
import { Link } from 'react-router-dom'
import { SchemaOutput } from '@/core/schema-renderer/SchemaOutput'
import { CopyButton, formatDuration } from '@/shared/components'
import type { ActionSchema, ExecutionResult } from '@/core/types/api.types'

export function ActionResult({
  result,
  schema,
}: {
  result: ExecutionResult
  schema?: ActionSchema
}) {
  const [showRequest, setShowRequest] = useState(false)

  const count = Array.isArray(result.output) ? result.output.length : null

  return (
    <div className="stack" style={{ gap: 12 }}>
      <div className="row">
        <span className="badge badge-ok">
          <span className="dot" />
          {result.status_code ?? 200}
        </span>
        <span className="small muted">{formatDuration(result.duration_ms)}</span>
        {result.pages > 1 && (
          <span className="badge badge-accent">{result.pages} pages fetched</span>
        )}
        {count !== null && (
          <span className="small muted">
            {count} record{count === 1 ? '' : 's'}
          </span>
        )}
        <div className="spacer" />
        <Link to={`/executions/${result.execution_id}`} className="small">
          Open in history
        </Link>
      </div>

      <SchemaOutput output={result.output} schema={schema?.output_schema} />

      {/* The exact request that left the server, with secrets already stripped.
          This is the single most useful thing when a template did not render
          what you expected. */}
      <div>
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => setShowRequest((v) => !v)}
          type="button"
        >
          {showRequest ? '▾' : '▸'} What was actually sent
        </button>
        {showRequest && (
          <div className="stack" style={{ gap: 6, marginTop: 6 }}>
            <div className="row small">
              <span className="method" data-m={result.request_preview.method}>
                {result.request_preview.method}
              </span>
              <span className="mono truncate" style={{ flex: 1 }}>
                {result.request_preview.url}
              </span>
              <CopyButton value={result.request_preview.url ?? ''} label="Copy URL" />
            </div>
            <pre className="json">{JSON.stringify(result.request_preview, null, 2)}</pre>
            <p className="small faint" style={{ margin: 0 }}>
              Credentials are redacted before this is recorded or shown.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
